import React, { useState } from 'react';
import { format } from 'date-fns';
import { Wallet, ArrowDownLeft, History, Loader2, ArrowDown, ArrowUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useClientTransactions } from '@/hooks/use-client-wallet';
import { handleTopUp } from '@/hooks/use-stripe-checkout-ui';

interface WalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId?: string;
  walletBalance?: number;
}

export const WalletDialog: React.FC<WalletDialogProps> = ({
  open,
  onOpenChange,
  clientId,
  walletBalance = 0,
}) => {
  const [depositAmount, setDepositAmount] = useState<number>(1);
  const [depositReference, setDepositReference] = useState<string>('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [activeTab, setActiveTab] = useState<'deposit' | 'history'>('deposit');
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);

  const { data: transactions = [], isLoading: isLoadingTransactions } = useClientTransactions();

  const handleTabChange = (value: string) => {
    setActiveTab(value as 'deposit' | 'history');
    if (value === 'history' && !hasLoadedHistory) {
      setHasLoadedHistory(true);
    }
  };

  const handleDepositSubmit = async () => {
    if (!depositAmount || depositAmount < 1) {
      toast.error('Minimum deposit is £1');
      return;
    }
    if (!clientId) {
      toast.error('Could not retrieve client information');
      return;
    }
    try {
      setProcessingPayment(true);
      onOpenChange(false);
      await handleTopUp(depositAmount, clientId, depositReference || undefined);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment error occurred');
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatCurrency = (value: number) => `£${Math.abs(value).toLocaleString()}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            Wallet
          </DialogTitle>
          <DialogDescription>
            Balance: <span className="font-semibold text-foreground">{formatCurrency(walletBalance)}</span>
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="deposit" className="flex items-center gap-2">
              <ArrowDownLeft className="h-4 w-4" />
              Add Funds
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="deposit" className="mt-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="wallet-amount">Amount</Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                  <Input
                    id="wallet-amount"
                    type="number"
                    min="1"
                    className="pl-7"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(parseInt(e.target.value) || 0)}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">Minimum deposit: £1</p>
              </div>
              <div>
                <Label htmlFor="wallet-reference">Reference (Optional)</Label>
                <Input
                  id="wallet-reference"
                  type="text"
                  className="mt-2"
                  placeholder="e.g., Wedding project deposit"
                  value={depositReference}
                  onChange={(e) => setDepositReference(e.target.value)}
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Add a note to help you track this deposit
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDepositSubmit}
                  disabled={processingPayment || depositAmount < 1}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {processingPayment && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Proceed to Payment
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <div className="max-h-[350px] overflow-auto rounded-md border">
              {isLoadingTransactions ? (
                <div className="p-2 space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-2">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-5 w-12 rounded-full" />
                      <Skeleton className="h-4 w-24 flex-1" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  ))}
                </div>
              ) : transactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mb-2" />
                  <p>No transactions yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader className="bg-muted/50 sticky top-0">
                    <TableRow>
                      <TableHead className="w-[90px]">Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.slice(0, 20).map((tx) => {
                      const hasCustomRef = tx.reference && tx.reference.includes(' [cs_');
                      const displayReference = hasCustomRef
                        ? tx.reference.split(' [cs_')[0]
                        : null;
                      return (
                        <TableRow key={tx.fund_history_id}>
                          <TableCell className="text-xs text-muted-foreground">
                            {format(new Date(tx.created_date), 'MMM d')}
                          </TableCell>
                          <TableCell>
                            <span
                              className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                tx.type === 'incoming'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              )}
                            >
                              {tx.type === 'incoming' ? (
                                <ArrowDown className="mr-1 h-3 w-3" />
                              ) : (
                                <ArrowUp className="mr-1 h-3 w-3" />
                              )}
                              {tx.type === 'incoming' ? 'In' : 'Out'}
                            </span>
                          </TableCell>
                          <TableCell
                            className="text-xs text-muted-foreground max-w-[120px] truncate"
                            title={displayReference || undefined}
                          >
                            {displayReference || '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium text-sm">
                            <span className={tx.type === 'incoming' ? 'text-green-600' : 'text-red-600'}>
                              {tx.type === 'incoming' ? '+' : '-'}{formatCurrency(tx.amount)}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
            {transactions.length > 20 && (
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Showing 20 most recent transactions
              </p>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default WalletDialog;
